import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, shareReplay } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { CommonService } from '../../common-service';

/** Opção de UF para selects: id = sigla (ex.: "SP"), descricao = nome. */
export interface UfOption {
    id: string;
    descricao: string;
    descricaoExibicao: string;
    idUnidadeFederativa: number;
    idRegiaoGeografica: number | null;
}

export interface UnidadeFederativaItem {
    idUnidadeFederativa: number;
    uf: string;
    nome: string;
    idRegiaoGeografica: number | null;
}

export interface RegiaoGeograficaItem {
    idRegiaoGeografica: number;
    descricao: string;
}

/**
 * Fonte única de Unidade Federativa do sistema (db_usuario), via API Usuario.
 * Substitui o antigo lookup de UF do Motor Fiscal.
 */
@Injectable({ providedIn: 'root' })
export class UnidadeFederativaService extends CommonService {
    private readonly apiUrl: string = environment.urlApiUsuario + 'Lookup';
    private cache$?: Observable<UfOption[]>;
    private regioesCache$?: Observable<RegiaoGeograficaItem[]>;

    constructor(private httpClient: HttpClient) {
        super();
    }

    public getUnidadesFederativas(): Observable<UfOption[]> {
        if (!this.cache$) {
            this.cache$ = this.httpClient
                .get<{ list: UnidadeFederativaItem[]; isError?: boolean }>(`${this.apiUrl}/unidade-federativa`, {
                    ...this.addHeaderToken(),
                })
                .pipe(
                    map(res => (res?.list ?? []).map(u => ({
                        id: u.uf,
                        descricao: u.nome,
                        descricaoExibicao: `${u.nome} - ${u.uf}`,
                        idUnidadeFederativa: u.idUnidadeFederativa,
                        idRegiaoGeografica: u.idRegiaoGeografica ?? null,
                    }))),
                    shareReplay(1),
                    catchError(() => {
                        this.cache$ = undefined;
                        return of([] as UfOption[]);
                    })
                );
        }
        return this.cache$;
    }

    public getRegioesGeograficas(): Observable<RegiaoGeograficaItem[]> {
        if (!this.regioesCache$) {
            this.regioesCache$ = this.httpClient
                .get<{ list: RegiaoGeograficaItem[]; isError?: boolean }>(`${this.apiUrl}/regiao-geografica`, {
                    ...this.addHeaderToken(),
                })
                .pipe(
                    map(res => res?.list ?? []),
                    shareReplay(1),
                    catchError(() => {
                        this.regioesCache$ = undefined;
                        return of([] as RegiaoGeograficaItem[]);
                    })
                );
        }
        return this.regioesCache$;
    }
}
